import React, {useReducer, useState} from 'react';
import pt from 'prop-types';
import {Form} from 'react-final-form';
import setFieldData from 'final-form-set-field-data';
import {StyledForm, GroupWrap, GroupTitle} from './MarketoForm.styles';
import submitForm from './source/MarketoSource';
import submitCustomForm, {submitMwAppService} from './source/MarketoCustomSource';
import submitSalesForceForm from './source/SalesForceSource';
import MarketoValidationEngine from './MarketoValidationEngine';
import {MARKETO, MARKETO_CUSTOM, SALESFORCE, MW_APP_SERVICE} from './constant';
import {setValue} from './utils/mutators';
import MarketoField from './MarketoField';
import MarketoButtonToolbar from './MarketoButtonToolbar';
import {convertStringToNumber} from '@epic-core/common';
import {getLocale} from '@epic-core/common';

const onSubmit = async (dispatch, fieldGroups, values, preSubmitMutateFn, formId, formType, postSubmitFn, customEndpointUrl) => {
    dispatch({type: 'loading'});

    let finalValues = Object.assign({}, values);

    //Manipulate the values for selects
    fieldGroups.forEach(group => {
        const fields = group.fields || [];
        fields.forEach(field => {
            const id = field.id;
            if (field.omit) {
                delete finalValues[id];
                return;
            }
            if (field.validationArray && field.validationArray.includes('numeric')) {
                const current = finalValues[id] || '';
                finalValues[id] = convertStringToNumber(current)
            }
            if (id && field.component){
                if (field.component === 'select') {
                    const current = finalValues[id] || {};
                    finalValues[id] = current.value;
                } else if (field.component === 'select-multi') {
                    const current = finalValues[id] || [];
                    const currentValues = [];
                    current.forEach(c => {
                        if (typeof c.value !== 'undefined') {
                            currentValues.push(c.value);
                        }
                    });
                    finalValues[id] = currentValues.join(';');
                } else if (field.component === 'select-boolean') {
                    const current = finalValues[id] || {};
                    finalValues[id] = (current.value === 'yes');
                }
            }
        });
    });

    if (preSubmitMutateFn && typeof preSubmitMutateFn === 'function') {
        finalValues = preSubmitMutateFn(fieldGroups, finalValues);
    }
    let response;
    if (formType === SALESFORCE) {
        response = await submitSalesForceForm(finalValues, formId);
    } else if (formType === MARKETO_CUSTOM) {
        response = await submitCustomForm(finalValues, formId, customEndpointUrl);
    } else if (formType === MW_APP_SERVICE) {
        response = await submitMwAppService(finalValues, formId, customEndpointUrl);
    } else {
        response = await submitForm(finalValues, formId);
    }
    dispatch({type: 'success', payload: response});
    if (postSubmitFn && typeof postSubmitFn === 'function') {
        postSubmitFn(response);
    }
};

const reducer = (state, {type, payload}) => {
    switch (type) {
        case 'success': {
            if (payload && payload.success && payload.status === 200) {
                return Object.assign(
                    {
                        loading: false,
                        submitted: true
                    },
                    payload
                );
            } else if (payload.status >= 400) {
                return {
                    loading: false,
                    submitted: true,
                    success: false,
                    errorMsg: payload.errorMsg,
					errorCode: payload.errorCode,
                    status: payload.status,
                    errors: payload.errors || []
                };
            }
            return {
                loading: false,
                success: false,
                submitted: true,
                errors: payload.errors || []
            };
        }
        case 'reset': {
            return {
                loading: false,
                success: false,
                submitted: false,
                errors: []
            };
        }
        default:
            return {
                loading: true
            };
    }
};

const MarketoForm = ({
    account = {},
    autoComplete = '',
    customEndpointUrl = '',
    fieldAdjustments = {},
    fieldGroups = [],
    formId,
    formType = MARKETO,
    initalValues,
    loadingImg,
    loadingNode,
    messages = {},
    postSubmitFn,
    preSubmitMutateFn
}) => {
    const [state, dispatch] = useReducer(reducer, {loading: false});
    const {loading, success, submitted, status, errors, errorCode} = state;
    const [formValid, setFormValid] = useState(false);
    let _initialValues = {
        email: account.email,
        company: account.company,
        firstName: account.name,
        lastName: account.lastName,
        CountryCode: account.country,
        Epic_Account_ID__c: account.id || account.Epic_Account_ID__c,
        webLocaleViewedUponFormSubmission: getLocale()
    }
    if (initalValues) {
        _initialValues = initalValues
    }

    let emailIsReadOnly = false;
    if (account.email) {
        emailIsReadOnly = true;
    }

    return (
        <Form
            autoComplete={autoComplete}
            onSubmit={values => {
                onSubmit(dispatch, fieldGroups, values, preSubmitMutateFn, formId, formType, postSubmitFn, customEndpointUrl)
            }}
            mutators={{
                setFieldData,
                setValue
            }}
            initialValues={_initialValues}
            render={({handleSubmit, form, submitting, pristine, values, active}) => {
                const mutators = form.mutators;
                return (
                    <>
                        <StyledForm
                            onSubmit={e => {
                                e.preventDefault();
                                handleSubmit(e);
                            }}>
                            {fieldGroups.map(group => {
                                const fields = group.fields || [];
                                return (
                                    <GroupWrap key={group.id} className={`${group.type} group-wrap`} width={group.width}>
                                        <GroupTitle>{group.label}</GroupTitle>
                                        {fields.map(field => {
                                            if (!field.id) return null;

                                            let adjustedField = field;
                                            if (field.id === 'email' && emailIsReadOnly) {
                                                adjustedField.readonly = true;
                                            }
                                            if (fieldAdjustments[field.id]) {
                                                adjustedField = Object.assign({}, field, fieldAdjustments[field.id]);
                                            }

                                            return (
                                                <MarketoField key={`field-${field.id}`} field={adjustedField} mutators={mutators} active={active} values={values} />
                                            );
                                        })}
                                    </GroupWrap>
                                );
                            })}
                            <MarketoButtonToolbar
                                loading={loading}
                                reset={(e) => {
                                    dispatch({type: 'reset'});

                                    fieldGroups.forEach(group => {
                                        const fields = group.fields || [];
                                        fields.forEach(field => {
                                            const id = field.id;
                                            if (!id) return;

                                            mutators.setFieldData(id, {touched: false});

                                            //reset hidden fields
                                            if (field.initHidden) {
                                                mutators.setFieldData(id, {hidden: true});
                                            }

                                            //reset values
                                            if (field.component === 'select' || field.component === 'select-multi') {
                                                mutators.setValue(id, _initialValues[id] || undefined);
                                            } else if (field.component === 'input' && field.type === 'text') {
                                                mutators.setValue(id, _initialValues[id] || '');
                                            } else if (field.component === 'input' && field.type === 'checkbox') {
                                                mutators.setValue(id, _initialValues[id] || false);
                                            }
                                        });
                                    });
                                    form.restart(_initialValues);
                                }}
                                success={success}
                                errors={errors || []}
								errorCode={errorCode}
                                submitted={submitted}
                                formValid={formValid}
                                status={status}
                                successBtnLabel={messages.successBtnLabel}
                                resetBtnLabel={messages.resetBtnLabel}
                                successMsg={messages.successMsg}
                                failureMsg={messages.failureMsg}
                                submissionDisclaimer={messages.submissionDisclaimer}
                                loadingImg={loadingImg}
                                loadingNode={loadingNode} />
                        </StyledForm>
                        <MarketoValidationEngine
                            fieldGroups={fieldGroups}
                            setFormValid={setFormValid}
                            setFieldData={form.mutators.setFieldData}
                            requiredError={messages.requiredError}
                            maxLengthError={messages.maxLengthError}
                            getFieldState={form.getFieldState} />
                    </>
                );
            }}
        />
    );
};

MarketoForm.propTypes = {
    fieldGroups: pt.array,
    initalValues: pt.object,
    account: pt.object,
    preSubmitMutateFn: pt.func,
    fieldAdjustments: pt.object,
    formId: pt.string.isRequired,
    messages: pt.object,
    loadingImg: pt.string,
    loadingNode: pt.node,
    formType: pt.string,
    postSubmitFn: pt.func,
    autoComplete: pt.string,
    customEndpointUrl: pt.string
};

export default MarketoForm;
